import { ReactNode } from 'react';

import classNames from 'classnames';
import { useTranslations } from 'next-intl';

import { MembershipDistributionPlanType, PublicStrategy } from '@/api/copy-trading/types';
import { FormatPercent } from '@/blocks/formatters';
import typography from '@/styles/typography.module.scss';

import * as PlanIcons from './PlatTypeIcon';
import { Card } from '../../Components/Card';
import styles from './styles.module.scss';

function PlanIcon({
  type,
}: {
  type: (typeof MembershipDistributionPlanType)[keyof typeof MembershipDistributionPlanType];
}) {
  const plansIcons: Record<
    (typeof MembershipDistributionPlanType)[keyof typeof MembershipDistributionPlanType],
    ReactNode
  > = {
    standard: <PlanIcons.Standard />,
    advanced: <PlanIcons.Advanced />,
    premium: <PlanIcons.Premium />,
    elite: <PlanIcons.Elite />,
  };

  return plansIcons[type] || <PlanIcons.Standard />;
}

export function ProfitDistributionPanel({ strategy }: { strategy: PublicStrategy }) {
  const tr = useTranslations();

  const planTypeI18n = {
    standard: tr.rich('pages.strategyPage.sections.main.widgets.profitDistribution.table.planType.standard'),
    advanced: tr.rich('pages.strategyPage.sections.main.widgets.profitDistribution.table.planType.advanced'),
    premium: tr.rich('pages.strategyPage.sections.main.widgets.profitDistribution.table.planType.premium'),
    elite: tr.rich('pages.strategyPage.sections.main.widgets.profitDistribution.table.planType.elite'),
  };

  return (
    <Card
      title={tr.rich('pages.strategyPage.sections.main.widgets.profitDistribution.title')}
      innerContentMaxWidthDesktop={328}
    >
      <table className={styles.table}>
        <thead>
          <tr className={classNames(typography.bodyXS, styles.wrapperHeaders)}>
            <th scope={'col'}>
              <span>
                {tr.rich(
                  'pages.strategyPage.sections.main.widgets.profitDistribution.table.headers.membership',
                )}
              </span>
            </th>
            <th scope={'col'}>
              <span>
                {tr.rich(
                  'pages.strategyPage.sections.main.widgets.profitDistribution.table.headers.follower',
                )}
              </span>
            </th>
            <th scope={'col'}>
              <span>
                {tr.rich(
                  'pages.strategyPage.sections.main.widgets.profitDistribution.table.headers.platform',
                )}
              </span>
            </th>
            <th scope={'col'}>
              <span>
                {tr.rich(
                  'pages.strategyPage.sections.main.widgets.profitDistribution.table.headers.provider',
                )}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {strategy.membershipDistribution.map((plan) => (
            <tr key={plan.planType}>
              <th scope={'row'}>
                <div>
                  <PlanIcon type={plan.planType} />
                  {planTypeI18n[plan.planType]}
                </div>
              </th>
              <td>
                <FormatPercent value={100 - plan.markupPercent - plan.traderPercent} />
              </td>
              <td>
                <FormatPercent value={plan.markupPercent} />
              </td>
              <td>
                <FormatPercent value={plan.traderPercent} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
